import React from "react";
import { View, Text, StyleSheet, Platform } from 'react-native';

import { t } from "../../../i18n/translations";
import PremiumButton from '../../../shared/ui/PremiumButton';
import { useLanguageStore } from "../../quiz-core/languageStore";

const IQES = {
  panel: "#0B1026",
  panelLift: "#151B37",
  gold: "#F8C84E",
  goldSoft: "#FFE78A",
  purpleDeep: "#21103D",
  ivory: "#FFF7DF",
  muted: "#C9BEDA",
  border: "rgba(248, 200, 78, 0.42)",
};

type ResultsActionsBlockMode = 'single' | 'multi';

interface ResultsActionsBlockProps {
  mode: ResultsActionsBlockMode;
  onPlayAnotherQuiz: () => void;
  onSecondaryAction?: () => void;
  showPrimaryAction?: boolean;
  secondaryText?: string;
  primaryText?: string;
  eyebrowText?: string;
  titleText?: string;
  subtitleText?: string;
  helperText?: string;
}

const ResultsActionsBlock: React.FC<ResultsActionsBlockProps> = ({
  mode,
  onPlayAnotherQuiz,
  onSecondaryAction,
  showPrimaryAction = true,
  secondaryText,
  primaryText,
  eyebrowText,
  titleText,
  subtitleText,
  helperText,
}) => {
  const { language } = useLanguageStore();
  const resolvedPrimaryText = primaryText || (mode === 'single'
    ? t('results.continueJourney', language)
    : t('results.playAgain', language));
  
  return (
    <View style={styles.container}>
      {eyebrowText ? (
        <View style={styles.eyebrowWrap}>
          <View style={styles.eyebrowPill}>
            <View style={styles.eyebrowDot} />
            <Text style={styles.eyebrowText}>{eyebrowText}</Text>
          </View>
        </View>
      ) : null}
      {titleText ? <Text style={styles.titleText}>{titleText}</Text> : null}
      {subtitleText ? <Text style={styles.subtitleText}>{subtitleText}</Text> : null}
      {showPrimaryAction ? (
        <View style={styles.actionButton}>
          <PremiumButton
            text={resolvedPrimaryText}
            iconName="arrow-forward"
            iconPosition="right"
            onPress={onPlayAnotherQuiz}
            gradientColors={[IQES.goldSoft, IQES.gold]}
            style={styles.buttonStyle}
            textStyle={styles.buttonText}
            accessibilityLabel={resolvedPrimaryText}
          />
        </View>
      ) : null}
      {onSecondaryAction && secondaryText ? (
        <View style={styles.secondaryButtonWrap}>
          <PremiumButton
            text={secondaryText}
            iconName="arrow-back"
            iconPosition="left"
            onPress={onSecondaryAction}
            gradientColors={[IQES.panelLift, IQES.panelLift]}
            style={styles.secondaryButton}
            textStyle={styles.secondaryText}
            accessibilityLabel={secondaryText}
          />
        </View>
      ) : null}
      {helperText ? <Text style={styles.helperText}>{helperText}</Text> : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    alignItems: 'center',
    maxWidth: 380,
    alignSelf: 'center',
    width: '100%',
    backgroundColor: IQES.panel,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: IQES.border,
    ...Platform.select({
      ios: {
        shadowColor: IQES.gold,
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.13,
        shadowRadius: 10,
      },
      android: {
        elevation: 2,
      },
      web: {
        boxShadow: '0 4px 14px rgba(248, 200, 78, 0.13)',
      },
    }),
  },
  eyebrowWrap: {
    width: '100%',
    marginBottom: 10,
  },
  eyebrowPill: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: IQES.panelLift,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: IQES.border,
  },
  eyebrowDot: {
    width: 7,
    height: 7,
    borderRadius: 3.5,
    backgroundColor: IQES.gold,
    marginRight: 7,
  },
  eyebrowText: {
    fontSize: 12,
    fontWeight: '700',
    color: IQES.goldSoft,
  },
  actionButton: {
    width: '100%',
  },
  titleText: {
    width: '100%',
    fontSize: 20,
    fontWeight: '800',
    color: IQES.ivory,
    marginBottom: 6,
  },
  subtitleText: {
    width: '100%',
    fontSize: 14,
    lineHeight: 20,
    color: IQES.muted,
    marginBottom: 14,
  },
  secondaryButtonWrap: {
    width: '100%',
    marginTop: 8,
  },
  buttonStyle: {
    width: '100%',
    paddingVertical: 15,
    borderRadius: 16,
    marginTop: 0,
    ...Platform.select({
      ios: {
        shadowColor: IQES.gold,
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.04,
        shadowRadius: 8,
      },
      android: {
        elevation: 1,
      },
      web: {
        boxShadow: '0 3px 10px rgba(248, 200, 78, 0.12)',
      },
    }),
  },
  buttonText: {
    fontSize: 17,
    fontWeight: '800',
    letterSpacing: 0.2,
    color: IQES.purpleDeep,
  },
  secondaryButton: {
    width: '100%',
    paddingVertical: 13,
    borderRadius: 16,
    marginTop: 0,
    borderWidth: 1,
    borderColor: IQES.border,
    ...Platform.select({
      ios: {
        shadowOpacity: 0,
      },
      android: {
        elevation: 0,
      },
      web: {
        boxShadow: 'none',
      },
    }),
  },
  secondaryText: {
    fontSize: 15,
    fontWeight: '700',
    letterSpacing: 0.2,
    color: IQES.goldSoft,
  },
  helperText: {
    width: '100%',
    marginTop: 10,
    fontSize: 13,
    lineHeight: 18,
    color: IQES.muted,
  },
});

export default ResultsActionsBlock;
